import { TrendingUp, Users, Eye, FileText, FolderKanban, BarChart3 } from 'lucide-react';
import { useTheme } from '@/context/ThemeContext';

const Analytics = () => {
  const { isDark } = useTheme();
  const metrics = [
    { label: 'Total Views', value: '12.5K', change: '+15%', icon: Eye, color: 'bg-blue-500' },
    { label: 'Active Users', value: '1.2K', change: '+8%', icon: Users, color: 'bg-green-500' },
    { label: 'Problem Statements', value: '24', change: '+3', icon: FileText, color: 'bg-purple-500' },
    { label: 'Projects', value: '48', change: '+5', icon: FolderKanban, color: 'bg-orange-500' },
    { label: 'Applications', value: '156', change: '+12%', icon: TrendingUp, color: 'bg-pink-500' },
    { label: 'Engagement Rate', value: '68%', change: '+5%', icon: BarChart3, color: 'bg-indigo-500' },
  ];

  const topProjects = [
    { name: 'EcoTrack', views: 1250, applications: 15 },
    { name: 'SkillSwap', views: 980, applications: 12 },
    { name: 'LocalVoice', views: 750, applications: 8 },
    { name: 'MediRemind', views: 620, applications: 10 },
  ];

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div>
        <h2 className={`text-2xl sm:text-3xl font-bold mb-2 transition-colors ${
          isDark ? 'text-white' : 'text-gray-900'
        }`}>Analytics</h2>
        <p className={`text-sm sm:text-base ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Platform insights and performance metrics</p>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {metrics.map((metric) => {
          const Icon = metric.icon;
          return (
            <div
              key={metric.label}
              className={`rounded-xl p-6 shadow-md border transition-colors ${
                isDark ? 'bg-gradient-to-br from-[#2d1b4e] to-[#1a0b2e] border-purple-500/20 hover:border-purple-500/40' : 'bg-white border-gray-200'
              }`}
            >
              <div className="flex items-center justify-between mb-4">
                <div className={`${metric.color} p-3 rounded-lg`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <span className="text-sm font-medium text-green-600">{metric.change}</span>
              </div>
              <p className={`text-sm font-medium mb-1 transition-colors ${
                isDark ? 'text-gray-400' : 'text-gray-600'
              }`}>{metric.label}</p>
              <p className={`text-3xl font-bold transition-colors ${
                isDark ? 'text-white' : 'text-gray-900'
              }`}>{metric.value}</p>
            </div>
          );
        })}
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Top Projects */}
        <div className={`rounded-xl p-6 shadow-md border transition-colors ${
          isDark ? 'bg-gradient-to-br from-[#2d1b4e] to-[#1a0b2e] border-purple-500/20' : 'bg-white border-gray-200'
        }`}>
          <h3 className={`text-xl font-semibold mb-4 transition-colors ${
            isDark ? 'text-white' : 'text-gray-900'
          }`}>Top Projects</h3>
          <div className="space-y-4">
            {topProjects.map((project) => (
              <div key={project.name} className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className={`font-medium transition-colors ${
                    isDark ? 'text-white' : 'text-gray-900'
                  }`}>{project.name}</span>
                  <span className={`text-sm transition-colors ${
                    isDark ? 'text-gray-300' : 'text-gray-600'
                  }`}>{project.views} views</span>
                </div>
                <div className={`w-full rounded-full h-2 transition-colors ${
                  isDark ? 'bg-gray-700' : 'bg-gray-200'
                }`}>
                  <div
                    className="bg-blue-500 h-2 rounded-full"
                    style={{ width: `${(project.views / 1250) * 100}%` }}
                  />
                </div>
                <div className={`flex items-center justify-between text-xs transition-colors ${
                  isDark ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  <span>{project.applications} applications</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Activity Overview */}
        <div className={`rounded-xl p-6 shadow-md border transition-colors ${
          isDark ? 'bg-gradient-to-br from-[#2d1b4e] to-[#1a0b2e] border-purple-500/20' : 'bg-white border-gray-200'
        }`}>
          <h3 className={`text-xl font-semibold mb-4 transition-colors ${
            isDark ? 'text-white' : 'text-gray-900'
          }`}>Activity Overview</h3>
          <div className="space-y-4">
            <div className={`flex items-center justify-between p-3 rounded-lg transition-colors ${
              isDark ? 'bg-purple-500/10 border border-purple-500/20' : 'bg-gray-50'
            }`}>
              <div>
                <p className={`font-medium transition-colors ${
                  isDark ? 'text-white' : 'text-gray-900'
                }`}>This Week</p>
                <p className={`text-sm transition-colors ${
                  isDark ? 'text-gray-400' : 'text-gray-500'
                }`}>Nov 11 - Nov 17</p>
              </div>
              <div className="text-right">
                <p className={`text-2xl font-bold transition-colors ${
                  isDark ? 'text-white' : 'text-gray-900'
                }`}>1.2K</p>
                <p className="text-xs text-green-600">+15% from last week</p>
              </div>
            </div>
            <div className={`flex items-center justify-between p-3 rounded-lg transition-colors ${
              isDark ? 'bg-purple-500/10 border border-purple-500/20' : 'bg-gray-50'
            }`}>
              <div>
                <p className={`font-medium transition-colors ${
                  isDark ? 'text-white' : 'text-gray-900'
                }`}>This Month</p>
                <p className={`text-sm transition-colors ${
                  isDark ? 'text-gray-400' : 'text-gray-500'
                }`}>November 2025</p>
              </div>
              <div className="text-right">
                <p className={`text-2xl font-bold transition-colors ${
                  isDark ? 'text-white' : 'text-gray-900'
                }`}>4.8K</p>
                <p className="text-xs text-green-600">+22% from last month</p>
              </div>
            </div>
            <div className={`flex items-center justify-between p-3 rounded-lg transition-colors ${
              isDark ? 'bg-purple-500/10 border border-purple-500/20' : 'bg-gray-50'
            }`}>
              <div>
                <p className={`font-medium transition-colors ${
                  isDark ? 'text-white' : 'text-gray-900'
                }`}>All Time</p>
                <p className={`text-sm transition-colors ${
                  isDark ? 'text-gray-400' : 'text-gray-500'
                }`}>Total platform views</p>
              </div>
              <div className="text-right">
                <p className={`text-2xl font-bold transition-colors ${
                  isDark ? 'text-white' : 'text-gray-900'
                }`}>45.2K</p>
                <p className={`text-xs transition-colors ${
                  isDark ? 'text-gray-400' : 'text-gray-500'
                }`}>Since launch</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics;

